import { useState, useEffect, useCallback } from 'react';

interface UseAudioPlayerReturn {
  play: (url: string) => void;
  pause: () => void;
  stop: () => void;
  resume: () => void;
  isPlaying: boolean;
  isPaused: boolean;
  currentTime: number;
  duration: number;
}

export const useAudioPlayer = (): UseAudioPlayerReturn => {
  const [audio] = useState(() => new Audio());
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentUrl, setCurrentUrl] = useState<string>('');

  const play = useCallback((url: string) => {
    if (isPaused && url === currentUrl) {
      audio.play();
      setIsPaused(false);
      setIsPlaying(true);
    } else {
      audio.src = url;
      setCurrentUrl(url);
      audio.play();
      setIsPlaying(true);
      setIsPaused(false);
    }
  }, [audio, isPaused, currentUrl]);

  const pause = useCallback(() => {
    audio.pause();
    setIsPlaying(false);
    setIsPaused(true);
  }, [audio]);

  const resume = useCallback(() => {
    if (isPaused) {
      audio.play();
      setIsPlaying(true);
      setIsPaused(false);
    }
  }, [audio, isPaused]);

  const stop = useCallback(() => {
    audio.pause();
    audio.currentTime = 0;
    setIsPlaying(false);
    setIsPaused(false);
    setCurrentTime(0);
  }, [audio]);

  useEffect(() => {
    const handleEnded = () => {
      setIsPlaying(false);
      setIsPaused(false);
      setCurrentTime(0);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleDurationChange = () => {
      setDuration(audio.duration);
    };

    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('durationchange', handleDurationChange);

    return () => {
      audio.pause();
      audio.src = '';
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('durationchange', handleDurationChange);
    };
  }, [audio]);

  return { play, pause, stop, resume, isPlaying, isPaused, currentTime, duration };
};
